import { useEffect, useMemo, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { MapPersistedState } from '../Map.types'

interface UseMapStatePersistenceParams {
  /** Mapbox map instance */
  map: mapboxgl.Map | null

  /** Context identifier for storage key (e.g., 'analysis-land', 'explore') */
  context: string

  /** Current map state to persist */
  currentState: MapPersistedState

  /** Debounce delay in milliseconds (default: 500) */
  debounceMs?: number
}

interface UseMapStatePersistenceReturn {
  /** Saved state loaded from localStorage (null if no saved state exists) */
  savedState: MapPersistedState | null

  /** Manually save current state to localStorage */
  saveState: (state: MapPersistedState) => void
}

/**
 * Hook for automatically persisting and restoring map state to/from localStorage
 *
 * @example
 * ```tsx
 * const { savedState } = useMapStatePersistence({
 *   map,
 *   context: 'analysis-land',
 *   currentState: {
 *     center: map?.getCenter().toArray(),
 *     zoom: map?.getZoom(),
 *     dimension,
 *     mapStyle,
 *     panelStates: { layers: layersPanelOpen }
 *   }
 * })
 *
 * // Use savedState to restore on mount
 * useEffect(() => {
 *   if (savedState && map) {
 *     if (savedState.center && savedState.zoom) {
 *       map.jumpTo({
 *         center: savedState.center,
 *         zoom: savedState.zoom,
 *         bearing: savedState.bearing,
 *         pitch: savedState.pitch
 *       })
 *     }
 *   }
 * }, [savedState, map])
 * ```
 */
export const useMapStatePersistence = ({
  map,
  context,
  currentState,
  debounceMs = 500,
}: UseMapStatePersistenceParams): UseMapStatePersistenceReturn => {
  // Generate storage key based on context
  const storageKey = useMemo(() => `map-state-${context}`, [context])

  // Load saved state from localStorage on mount
  const [savedState] = useState<MapPersistedState | null>(() => {
    if (!context) return null

    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored) as MapPersistedState
      }
    } catch (error) {
      console.error(`Failed to load map state from localStorage for context "${context}":`, error)
    }

    return null
  })

  // Manual save function
  const saveState = (state: MapPersistedState) => {
    if (!context) return

    try {
      localStorage.setItem(storageKey, JSON.stringify(state))
    } catch (error) {
      console.error(`Failed to save map state to localStorage for context "${context}":`, error)
    }
  }

  // Debounced auto-save effect
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    // Don't auto-save if context is not provided or map is not ready
    if (!context || !map) return

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout for debounced save
    timeoutRef.current = setTimeout(() => {
      saveState(currentState)
    }, debounceMs)

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [context, map, currentState, debounceMs])

  return {
    savedState,
    saveState,
  }
}
